import { Check, ChevronDown } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';

import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import type { PaginationActions, PaginationState } from '@/hooks/usePagination';
import { cn } from '@/lib/utils';
import { TaskCountsResponse } from '@/services/api/tasksService';
import {
  CanonicalTaskGroup,
  getTaskGroupDisplayName,
} from '@/services/types/task-types';

import { ACTIVE_STATUS_VALUES } from '../config/filterConfig';
import { GROUP_DISPLAY_ORDER } from '../config/taskVerticals';

interface TaskCanonicalGroupPillsProps {
  /** Current pagination state including active filters */
  paginationState: PaginationState;
  /** Actions for updating pagination state and filters */
  paginationActions: PaginationActions;
  /** Task counts data passed from parent */
  countsData?: TaskCountsResponse;
}

/**
 * TaskCanonicalGroupPills component for filtering tasks by canonical group/type.
 *
 * Provides hierarchical filtering with group pills that expand to show types.
 * Supports multi-select of types across groups.
 * Updates 'canonical_type_ids' (UUIDs) and 'status' filters to sync with pill counts (active tasks only).
 */
export const TaskCanonicalGroupPills: React.FC<
  TaskCanonicalGroupPillsProps
> = ({ paginationState, paginationActions, countsData }) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useOnClickOutside(containerRef, () => {
    setOpenDropdown(null);
  });

  // Get current canonical_type_ids filters from URL (array of UUIDs)
  const currentCanonicalTypeIds = useMemo(() => {
    const val = paginationState.filters.canonical_type_ids;
    if (Array.isArray(val)) return val as string[];
    if (typeof val === 'string') return [val];
    return [] as string[];
  }, [paginationState.filters]);

  // Check if a type is selected (by ID)
  const isTypeSelected = (id: string) => {
    return currentCanonicalTypeIds.includes(id);
  };

  // Get types for a specific group (sorted alphabetically by type name)
  const getTypesForGroup = (group: string) => {
    if (!countsData?.types) return [];
    return countsData.types
      .filter((t) => t.group === group)
      .sort((a, b) => a.type.localeCompare(b.type));
  };

  // Get selected type IDs for a specific group
  const getSelectedTypeIdsForGroup = (group: string) => {
    const groupTypes = getTypesForGroup(group);
    return groupTypes.filter((t) => isTypeSelected(t.id)).map((t) => t.id);
  };

  // Update filters helper (uses canonical_type_ids)
  const updateFilters = (newTypeIds: string[]) => {
    // Remove legacy filters and current type filters before applying new ones
    const {
      group_types: _legacyGroupTypes,
      canonical_type_ids: _currentTypeIds,
      status: _status,
      ...otherFilters
    } = paginationState.filters;

    if (newTypeIds.length === 0) {
      paginationActions.setFilters(otherFilters);
    } else {
      // Pill counts represent active tasks, so apply active status filter
      paginationActions.setFilters({
        ...otherFilters,
        canonical_type_ids: newTypeIds,
        status: [...ACTIVE_STATUS_VALUES],
      });
    }
  };

  // Handle group pill click
  const handleGroupClick = (group: string) => {
    const groupTypes = getTypesForGroup(group);
    const selectedIdsInGroup = getSelectedTypeIdsForGroup(group);

    if (selectedIdsInGroup.length > 0) {
      // If any types from this group are selected, clear them all
      // Keep types from OTHER groups
      const groupTypeIds = groupTypes.map((t) => t.id);
      const newTypeIds = currentCanonicalTypeIds.filter(
        (id) => !groupTypeIds.includes(id),
      );
      updateFilters(newTypeIds);
    } else {
      // No types selected from this group, select all types in the group
      const allTypeIdsInGroup = groupTypes.map((t) => t.id);
      // Add these to existing selections (avoiding duplicates)
      const newTypeIds = Array.from(
        new Set([...currentCanonicalTypeIds, ...allTypeIdsInGroup]),
      );
      updateFilters(newTypeIds);
    }
  };

  // Handle type selection from dropdown (by ID)
  const handleTypeClick = (id: string) => {
    const isSelected = isTypeSelected(id);
    let newTypeIds: string[];

    if (isSelected) {
      // Deselect this type
      newTypeIds = currentCanonicalTypeIds.filter((t) => t !== id);
    } else {
      // Select this type
      newTypeIds = [...currentCanonicalTypeIds, id];
    }

    updateFilters(newTypeIds);
  };

  // Handle clear all types in dropdown
  const handleClearAllInGroup = (group: string) => {
    const groupTypes = getTypesForGroup(group);
    const groupTypeIds = groupTypes.map((t) => t.id);

    // Remove all types belonging to this group
    const newTypeIds = currentCanonicalTypeIds.filter(
      (id) => !groupTypeIds.includes(id),
    );

    updateFilters(newTypeIds);
    setOpenDropdown(null);
  };

  // Show pills immediately using static GROUP_DISPLAY_ORDER.
  // Counts fill in once metadata loads (loading shimmer until then).
  const countsLoaded = !!countsData?.groups;

  const groupsToDisplay = useMemo(() => {
    if (countsLoaded && countsData?.groups) {
      // Once loaded, use backend groups (may include extra groups or exclude empty ones)
      return [...countsData.groups].sort((a, b) => {
        const aIndex = GROUP_DISPLAY_ORDER.indexOf(
          a.canonical_group as CanonicalTaskGroup,
        );
        const bIndex = GROUP_DISPLAY_ORDER.indexOf(
          b.canonical_group as CanonicalTaskGroup,
        );
        const aOrder = aIndex === -1 ? GROUP_DISPLAY_ORDER.length : aIndex;
        const bOrder = bIndex === -1 ? GROUP_DISPLAY_ORDER.length : bIndex;
        return aOrder - bOrder;
      });
    }
    // Before metadata loads, show all known groups as placeholders
    return GROUP_DISPLAY_ORDER.map((group) => ({
      canonical_group: group,
      count: 0,
    }));
  }, [countsData, countsLoaded]);

  return (
    <div className="flex flex-wrap gap-2" ref={containerRef}>
      {groupsToDisplay.map((groupData) => {
        const group = groupData.canonical_group;
        const groupCount = groupData.count;
        const types = getTypesForGroup(group);
        const selectedIdsInGroup = getSelectedTypeIdsForGroup(group);
        const isGroupActive = selectedIdsInGroup.length > 0;
        const isOpen = openDropdown === group;

        return (
          <div
            key={group}
            className={cn(
              'relative flex items-center rounded-md border transition-all duration-200 h-7', // h-7 for compact size
              isGroupActive
                ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                : 'bg-white border-gray-200 hover:border-gray-300 dark:bg-zinc-900 dark:border-zinc-800 dark:hover:border-zinc-700',
            )}
          >
            {/* Main Group Pill */}
            <button
              type="button"
              onClick={() => handleGroupClick(group)}
              className={cn(
                'flex items-center gap-2 px-2.5 h-full rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-black',
                // Text color
                isGroupActive
                  ? 'text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-zinc-700 dark:text-zinc-300 font-medium',
              )}
              title={
                isGroupActive
                  ? `${getTaskGroupDisplayName(group)} - ${selectedIdsInGroup.length} types selected. Click to clear.`
                  : `Click to select all ${getTaskGroupDisplayName(group)} types`
              }
            >
              {/* Group name */}
              <span className="text-xs leading-none">
                {getTaskGroupDisplayName(group)}
              </span>

              {/* Total count badge */}
              <span
                className={cn(
                  'inline-flex items-center justify-center px-1.5 py-0.5 rounded text-xs font-medium min-w-[20px] tabular-nums leading-none',
                  isGroupActive
                    ? 'bg-white text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400',
                )}
              >
                {countsLoaded ? (
                  groupCount
                ) : (
                  <span className="inline-block w-4 h-3 rounded bg-gray-200 dark:bg-zinc-700 animate-pulse" />
                )}
              </span>
            </button>

            {/* Separator line between main button and chevron (only visible on hover or active) */}
            {types.length > 0 && (
              <div
                className={cn(
                  'w-px h-3.5',
                  isGroupActive
                    ? 'bg-blue-200 dark:bg-blue-800'
                    : 'bg-gray-200 dark:bg-zinc-800',
                )}
              />
            )}

            {/* Dropdown Trigger (only show if there are types) */}
            {types.length > 0 && (
              <button
                type="button"
                onClick={() => setOpenDropdown(isOpen ? null : group)}
                className={cn(
                  'px-1 h-full rounded-r-md flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-black',
                  isGroupActive
                    ? 'text-blue-600 hover:bg-blue-100/50 dark:text-blue-400 dark:hover:bg-blue-900/40'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 dark:text-zinc-500 dark:hover:text-zinc-300 dark:hover:bg-zinc-800',
                )}
                aria-label={`${isOpen ? 'Hide' : 'Show'} ${getTaskGroupDisplayName(group)} types`}
              >
                <ChevronDown
                  className={cn(
                    'size-3 transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
            )}

            {/* Dropdown Menu */}
            {isOpen && types.length > 0 && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-gray-200 dark:border-zinc-800 py-1 z-50 min-w-[240px] max-w-[320px]">
                {/* Header */}
                <div className="px-2 py-1.5 border-b border-gray-100 dark:border-zinc-800">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                      Types
                    </span>
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400 whitespace-nowrap">
                      {selectedIdsInGroup.length} selected
                    </span>
                  </div>
                </div>

                {/* Type Options */}
                <div className="py-1 max-h-[300px] overflow-y-auto">
                  {types.map((typeData) => {
                    const isSelected = isTypeSelected(typeData.id);

                    return (
                      <button
                        key={typeData.id}
                        type="button"
                        onClick={() => handleTypeClick(typeData.id)}
                        className={cn(
                          'w-full text-left px-2 py-1 text-xs flex items-center gap-2 transition-colors duration-150',
                          isSelected
                            ? 'bg-blue-50 text-blue-900 dark:bg-blue-900/20 dark:text-blue-100'
                            : 'hover:bg-gray-50 text-zinc-700 dark:hover:bg-zinc-800 dark:text-zinc-300',
                        )}
                        title={
                          isSelected
                            ? `Deselect ${typeData.type}`
                            : `Select ${typeData.type}`
                        }
                      >
                        {/* Checkmark icon - fixed width column */}
                        <div className="w-4 h-4 flex items-center justify-center shrink-0">
                          {isSelected && (
                            <Check className="size-3 text-blue-600 dark:text-blue-400 stroke-[2.5]" />
                          )}
                        </div>

                        {/* Type name - grows to fill space */}
                        <span className="font-medium truncate flex-1 min-w-0 leading-tight">
                          {typeData.type}
                        </span>

                        {/* Count badge - fixed width */}
                        <span
                          className={cn(
                            'inline-flex items-center justify-center px-1.5 py-0.5 rounded text-xs font-medium min-w-[24px] shrink-0 tabular-nums',
                            isSelected
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400',
                          )}
                        >
                          {typeData.count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Footer - Clear all button */}
                {selectedIdsInGroup.length > 0 && (
                  <div className="px-2 py-1 border-t border-gray-100 dark:border-zinc-800 bg-gray-50/50 dark:bg-zinc-900">
                    <button
                      type="button"
                      onClick={() => handleClearAllInGroup(group)}
                      className="w-full text-[10px] font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors text-center py-0.5"
                    >
                      Clear selection
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
